import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';


import { useTheme } from './ThemeContext';



const AUTH_TOKEN_KEY = 'forc.auth.token';

function getStoredAuthToken() {
  return localStorage.getItem(AUTH_TOKEN_KEY);
}

function setStoredAuthToken(token) {
  localStorage.setItem(AUTH_TOKEN_KEY, token);
}

function clearStoredAuthToken() {
  localStorage.removeItem(AUTH_TOKEN_KEY);
}


const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [token, setToken] = useState(() => getStoredAuthToken());
  const [user, setUser] = useState(null);
  const [loadingSession, setLoadingSession] = useState(Boolean(token));
  const { applyUserTheme } = useTheme();

  const establishSession = useCallback((session) => {
    setStoredAuthToken(session.token);
    setToken(session.token);
    setUser(session.user);
    applyUserTheme(session.user);
  }, [applyUserTheme]);

  const clearSession = useCallback(() => {
    clearStoredAuthToken();
    setToken(null);
    setUser(null);
  }, []);

  const refreshUser = useCallback(async () => {
    if (!token) {
      setLoadingSession(false);
      setUser(null);
      return null;
    }
    setLoadingSession(true);
    try {

      const Factory = require( '../records/auth' );

      const response = await Factory.getCurrentSessionUser();
      setUser(response.user);
      applyUserTheme(response.user);
      return response.user;
    } catch (error) {
      clearSession();
      return null;
    } finally {
      setLoadingSession(false);
    }
  }, [clearSession, token, applyUserTheme]);

  useEffect(() => {
    refreshUser();
  }, [refreshUser]);

  const login = useCallback(async ({ email, password }) => {

    const Factory = require( '../records/auth' );

    const session = await Factory.loginWithEmailPassword({ email, password });
    establishSession(session);
    return session.user;
  }, [establishSession]);

  const logout = useCallback(() => {
    clearSession();
  }, [clearSession]);

  const value = useMemo(() => ({
    token,
    user,
    loadingSession,
    isAuthenticated: Boolean(token && user),
    login,
    logout,
    refreshUser,
    establishSession,
  }), [token, user, loadingSession, login, logout, refreshUser, establishSession]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used inside AuthProvider');
  }
  return context;
}
